
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

/**
 * EntityFilterEngine — the Headlight-domain filter-composition half of the old Select2Util,
 * extracted verbatim so it can be shared without the select2 transport.
 *
 * These methods compose FoxHound scope clauses (Static / Dynamic `FromField` + per-row
 * `FromRowField` / SamplePlan / Through / Lab), split mandatory vs. back-off-able, and resolve
 * the Show-All override. They are select2-/DOM-free — they read live form state via
 * `this.pict.manifest` and delegate id-set resolution to the domain scope resolvers
 * (`SpecScopeResolver` / `ThroughScopeResolver` / `LabScopeResolver`), so they are unit-tested
 * directly against `this.pict` stubs.
 *
 * Two consumers share this one implementation:
 *   - `Select2Util` (the legacy select2 transport) **extends** this class, so its
 *     `getOptionsAjax` keeps calling `this.applyStaticFilters(...)` etc. unchanged.
 *   - the pict-section-picker `Picker` broker **calls** this via `this.pict.providers.EntityFilterEngine`
 *     from `getContextualSearchFilters`, feeding the composed clauses to PSP's `BaseFilter`.
 *
 * The manifest shapes (`PictForm.StaticFilters` / `DynamicFilters` / `ShowAllToggle` /
 * `OverrideField`) are unchanged — see docs/Filters.md — so no form edits are required.
 */
class EntityFilterEngineProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Append descriptor-level static scope filters to a select2 search filter
	 * list. Read from `pInput.PictForm.StaticFilters` — used to scope a picker
	 * to a polymorphic slice of its entity (e.g. `Organization` by `Type`,
	 * `Product` by `ProductCategory`). Applied on top of the per-provider
	 * `HandleSearchFilters` so the scope always constrains the result.
	 *
	 * Each entry is either:
	 *   - a structured `{ Field, Operator = 'EQ', Value }` (scalar) or
	 *     `{ Field, Operator = 'INN', Values: [...] }` (list) object whose
	 *     value(s) are URL-encoded here — the recommended form; or
	 *   - a ready Foxhound clause string, pushed verbatim (caller must
	 *     URL-encode any values — the `FilteredTo/<…>/` path rejects raw spaces).
	 *
	 * A filter entry flagged `BackOff: true` is routed to `pBackoff` (when provided) instead of
	 * `pFilters`, so the URL builder can drop it on the empty-result retry (see `getOptionsAjax`).
	 * Default static filters are deliberate scopes and stay mandatory.
	 *
	 * @param {string[]} pFilters - The mandatory filter list to append to.
	 * @param {object} pInput - The descriptor (reads `pInput.PictForm.StaticFilters`).
	 * @param {string[]} [pBackoff] - Optional back-off-able filter list for `BackOff: true` entries.
	 * @return {string[]} The (mutated) mandatory filter list.
	 */
	applyStaticFilters(pFilters, pInput, pBackoff)
	{
		pFilters = pFilters || [];
		const tmpStaticFilters = pInput && pInput.PictForm ? pInput.PictForm.StaticFilters : null;
		if (!Array.isArray(tmpStaticFilters))
		{
			return pFilters;
		}
		for (const tmpFilter of tmpStaticFilters)
		{
			if (typeof tmpFilter === 'string')
			{
				if (tmpFilter)
				{
					pFilters.push(tmpFilter);
				}
				continue;
			}
			if (!tmpFilter || !tmpFilter.Field)
			{
				continue;
			}
			const tmpTarget = (pBackoff && tmpFilter.BackOff === true) ? pBackoff : pFilters;
			const tmpOperator = tmpFilter.Operator || 'EQ';
			if (Array.isArray(tmpFilter.Values))
			{
				tmpTarget.push(`FBL~${tmpFilter.Field}~${tmpOperator}~${tmpFilter.Values.map((pValue) => encodeURIComponent(pValue)).join(',')}`);
			}
			else
			{
				tmpTarget.push(`FBV~${tmpFilter.Field}~${tmpOperator}~${encodeURIComponent(tmpFilter.Value)}`);
			}
		}
		return pFilters;
	}

	/**
	 * Append sibling-field-driven dynamic scope filters to a select2 search
	 * filter list, narrowing a picker to the entities associated with another
	 * field's current selection (e.g. Product scoped to the picked Producer:
	 * `Product.IDOrganization = <H.Producer>`). Read from
	 * `pInput.PictForm.DynamicFilters`.
	 *
	 * Each entry is `{ Field, FromField, Operator = 'EQ' }` where `FromField` is
	 * a pict address (e.g. `AppData.DocumentData.ReportData.FormData.H.Producer`)
	 * whose current value is read and URL-encoded into `FBV~Field~OP~<value>`.
	 * When the source value is unset — null, undefined, '', or the 1-based-id unset
	 * sentinel 0 / "0" — the filter is omitted (the picker shows its full set) rather
	 * than emitting `FBV~Field~EQ~0` (which would match nothing). The now-orphaned
	 * dependent value is cleared separately on the anchor's change (see
	 * MaterialsEntitySelector.onAfterEventCompletion), so an unset anchor never leaves
	 * a stale selection behind.
	 *
	 * Per-row tabular narrowing: when this picker is a column in a RecordSet table,
	 * `FromField` resolves a single global address — identical for every row — which
	 * is wrong when each row's anchor differs (e.g. each Aggregate Product row has its
	 * own Source/Producer). Use `FromRowField` instead: the sibling column's datum hash
	 * within the SAME row. It composes `<InformaryContainerAddress>[<pRowIndex>].<FromRowField>`
	 * and reads it from the marshal destination, so each row narrows by its OWN anchor.
	 * Requires `pRowIndex` and `pView` (threaded from MaterialsEntitySelector.initializeSelect2
	 * via the ajax options). Outside a tabular context (`pRowIndex == null`) a `FromRowField`
	 * entry without a `FromField` is skipped. `FromField` keeps working unchanged.
	 *
	 * @param {string[]} pFilters - The filter list to append to.
	 * @param {object} pInput - The descriptor (reads `pInput.PictForm.DynamicFilters`).
	 * @param {number} [pRowIndex] - The row index when this is a tabular column (enables `FromRowField`).
	 * @param {object} [pView] - The form view (provides the marshal destination for `FromRowField`).
	 * @param {string[]} [pBackoff] - Optional back-off-able filter list for `BackOff: true` entries (see `getOptionsAjax`).
	 * @return {string[]} The (mutated) mandatory filter list.
	 */
	applyDynamicFilters(pFilters, pInput, pRowIndex, pView, pBackoff)
	{
		pFilters = pFilters || [];
		const tmpDynamicFilters = pInput && pInput.PictForm ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpDynamicFilters))
		{
			return pFilters;
		}
		// Show All is a MANUAL OVERRIDE WITH THE HIGHEST PRIORITY: it drops every sibling-driven
		// narrowing on this picker — the direct `FromField` / `FromRowField` clauses that solvers and
		// autofill triggers drive, as well as the `SamplePlanScope` / `Through` / `LabScope` id-set
		// scopes — so the dropdown falls back to the scope it would have had with
		// NO other selections made. `StaticFilters`, project scoping and the selector's own intrinsic
		// `HandleSearchFilters` are not "other selections" and still apply. Evaluated once, here, WITH
		// the row index, so a tabular picker honors its own row's toggle rather than the flat address
		// (the per-shape guards below repeat the check for direct callers, and are unit-tested as such).
		if (this.isFilterOverridden(pInput, pRowIndex))
		{
			return pFilters;
		}
		const tmpIsTabular = (pRowIndex != null) && pView && pInput.PictForm && pInput.PictForm.InformaryContainerAddress;
		for (const tmpFilter of tmpDynamicFilters)
		{
			// Sampling-plan scope is a distinct shape: it reads one or more upstream
			// siblings and resolves an INN id-set from the plan, rather than a single
			// FromField EQ clause. Handle (and skip) it before the generic guard below.
			if (tmpFilter && tmpFilter.SamplePlanScope)
			{
				this._applySamplePlanFilter(pFilters, tmpFilter, pInput);
				continue;
			}
			// Through ("chained") scope: narrows by a relationship with no direct FK on
			// this entity, hopping a bridge table via ThroughScopeResolver. Like the
			// sampling-plan scope it resolves an INN id-set, not a FromField EQ clause.
			if (tmpFilter && tmpFilter.Through)
			{
				this._applyThroughFilter(pFilters, tmpFilter, pInput);
				continue;
			}
			// Lab scope: parent labs for the sample-level selector, the leaf labs that can run the test for a
			// per-test one, resolved by LabScopeResolver. Like the other scopes it resolves an INN id-set.
			if (tmpFilter && tmpFilter.LabScope)
			{
				this._applyLabScopeFilter(pFilters, tmpFilter, pInput, pRowIndex, pView);
				continue;
			}
			if (!tmpFilter || !tmpFilter.Field || (!tmpFilter.FromField && !tmpFilter.FromRowField))
			{
				continue;
			}
			let tmpValue;
			if (tmpIsTabular && tmpFilter.FromRowField)
			{
				// Same-row sibling: compose `<container>[<row>].<datum>` and read the live value
				// from the marshal destination, so this row narrows by its own anchor cell.
				const tmpRowAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, tmpFilter.FromRowField);
				tmpValue = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRowAddress);
			}
			else if (tmpFilter.FromField)
			{
				tmpValue = this.pict.manifest.getValueByHash(this.pict, tmpFilter.FromField);
			}
			else
			{
				// A FromRowField-only entry outside a tabular context — nothing to resolve against.
				continue;
			}
			// Treat an unset anchor as "no constraint" and OMIT the filter (show the full set),
			// rather than emitting `FBV~Field~EQ~0` which would match nothing. FK ids are 1-based,
			// so 0 / "0" is the unset sentinel (e.g. ProductionPlant.IDOrganization = 0); null /
			// undefined / "" are also unset. The dependent's stale value is cleared separately when
			// the anchor changes (see MaterialsEntitySelector.onAfterEventCompletion).
			const tmpNormalized = (tmpValue === null || tmpValue === undefined) ? '' : String(tmpValue).trim();
			if (tmpNormalized === '' || tmpNormalized === '0')
			{
				continue;
			}
			const tmpOperator = tmpFilter.Operator || 'EQ';
			// Sibling-driven narrowing flagged `BackOff: true` routes to the back-off-able list so the
			// picker shows its full set rather than an empty dropdown when the anchor has no matches.
			const tmpTarget = (pBackoff && tmpFilter.BackOff === true) ? pBackoff : pFilters;
			tmpTarget.push(`FBV~${tmpFilter.Field}~${tmpOperator}~${encodeURIComponent(tmpValue)}`);
		}
		return pFilters;
	}

	/**
	 * Apply a sampling-plan scope clause for a DynamicFilters entry carrying a
	 * `SamplePlanScope` ('LineItem' | 'Material' | 'Test'). Reads the sibling values
	 * named by the entry's `SampleTypeFromField` / `LineItemFromField` /
	 * `MaterialFromField` / `TestFromField` addresses — bidirectional: each axis
	 * narrows by every OTHER axis's current selection through the plan, not just the
	 * upstream ones. Asks SpecScopeResolver for the plan-allowed ids on that axis and
	 * pushes a mandatory `FBL~Field~INN~<ids>` clause.
	 *
	 * Pushed to the mandatory list (not the back-off list) so an empty plan-filtered
	 * result does NOT auto-widen — it stays constrained until the user toggles Show
	 * All, which drops it here via isFilterOverridden. Omitted (fail-open) when there
	 * is no plan (resolver returns null) or the upstream selection yields no plan ids
	 * on this axis (so the picker keeps its other scopes rather than forcing empty).
	 *
	 * @param {string[]} pFilters - the mandatory filter list (mutated)
	 * @param {object} pFilter - the DynamicFilters entry (reads SamplePlanScope, Field, *FromField)
	 * @param {object} pInput - the descriptor (for the Show All override check)
	 * @return {void}
	 */
	_applySamplePlanFilter(pFilters, pFilter, pInput)
	{
		if (this.isFilterOverridden(pInput))
		{
			return;
		}
		const tmpResolver = this.pict.providers.SpecScopeResolver;
		if (!tmpResolver || !pFilter.Field)
		{
			return;
		}
		const tmpRead = (pAddress) => (pAddress ? this.pict.manifest.getValueByHash(this.pict, pAddress) : undefined);
		const tmpSampleType = tmpRead(pFilter.SampleTypeFromField);
		const tmpIDLineItem = tmpRead(pFilter.LineItemFromField);
		const tmpIDTest = tmpRead(pFilter.TestFromField);
		// The Material anchor is PUSHED to the Test / LineItem axes — expand it UP so a leaf
		// selection also matches plan rows bound to its parent categories. (Material is the only
		// hierarchical axis.) Comma-joined: the plan getters are list-aware (_samplePlanIDs).
		const tmpMaterialAnchor = tmpRead(pFilter.MaterialFromField);
		const tmpIDMaterial = (tmpMaterialAnchor && tmpResolver.expandMaterialAnchorsUp) ?
			Array.from(tmpResolver.expandMaterialAnchorsUp(tmpMaterialAnchor)).join(',') :
			tmpMaterialAnchor;
		let tmpAllowed = null;
		switch (pFilter.SamplePlanScope)
		{
		case 'LineItem':
			tmpAllowed = tmpResolver.getSamplePlanLineItemIDs?.(tmpSampleType, tmpIDMaterial, tmpIDTest);
			break;
		case 'Material':
			tmpAllowed = tmpResolver.getSamplePlanMaterialIDs?.(tmpSampleType, tmpIDLineItem, tmpIDTest);
			// The Material axis is this dropdown's own filter — expand the allowed set DOWN so a
			// child material is selectable under an allowed (parent/category) plan material.
			if (tmpAllowed && tmpResolver.expandMaterialSetDown)
			{
				tmpAllowed = tmpResolver.expandMaterialSetDown(tmpAllowed);
			}
			break;
		case 'Test':
			tmpAllowed = tmpResolver.getSamplePlanTestIDs?.(tmpSampleType, tmpIDLineItem, tmpIDMaterial);
			break;
		default:
			return;
		}
		if (tmpAllowed && tmpAllowed.size > 0)
		{
			pFilters.push(`FBL~${pFilter.Field}~INN~${Array.from(tmpAllowed).join(',')}`);
		}
		else if (tmpAllowed)
		{
			// Non-null but EMPTY: the plan IS loaded and authoritatively has no rows for this selection
			// (e.g. a SampleType with no plan tests) — constrain to none rather than fail open to the full
			// list. A null tmpAllowed (no plan loaded at all) still fails open below, and Show All overrides
			// both via isFilterOverridden at the top.
			pFilters.push(this._constrainToNoneClause(pFilter, pInput));
		}
	}

	/**
	 * Zero-row constraint clause for a scope filter. Targets the entity PK (`ID<Entity>` from
	 * `pInput.PictForm.Entity`) — always a positive auto-increment, so `0` can never match — rather than the
	 * scope `Field`, whose `0` may be a legitimate value (a status, or a nullable / parent FK such as
	 * `IDLabParent` which is 0 for root records). Falls back to `Field` only when the Entity is unknown.
	 * @param {object} pFilter @param {object} pInput @return {string}
	 */
	_constrainToNoneClause(pFilter, pInput)
	{
		const tmpEntity = (pInput && pInput.PictForm) ? pInput.PictForm.Entity : null;
		// Scalar EQ~0 (the "matches nothing" idiom the applyDynamicFilters unset-anchor path documents) rather
		// than INN~0 — no list to sanitize, and self-documenting as a zero-row constraint.
		return `FBV~${tmpEntity ? ('ID' + tmpEntity) : pFilter.Field}~EQ~0`;
	}

	/**
	 * Apply a Through ("chained") scope clause for a DynamicFilters entry carrying a
	 * `Through` bridge config. Reads the live anchor at `FromField`, asks
	 * ThroughScopeResolver for the bridge-allowed ids on the picker's `Field`, and
	 * pushes a mandatory `FBL~Field~INN~<ids>` clause (dropped by Show All via
	 * isFilterOverridden). Fail-open: omitted when there is no resolver, no resolved
	 * id-set (slice not yet primed), or an empty set — the picker keeps its full
	 * list rather than forcing empty.
	 *
	 * @param {string[]} pFilters - the mandatory filter list (mutated)
	 * @param {object} pFilter - the DynamicFilters entry (Through, ThroughKey, ThroughSelect, FromField, Field, Strategy)
	 * @param {object} pInput - the descriptor (for the Show All override check)
	 * @return {void}
	 */
	_applyThroughFilter(pFilters, pFilter, pInput)
	{
		if (this.isFilterOverridden(pInput))
		{
			return;
		}
		const tmpResolver = this.pict.providers.ThroughScopeResolver;
		if (!tmpResolver || !pFilter.Field)
		{
			return;
		}
		const tmpAnchor = pFilter.FromField ? this.pict.manifest.getValueByHash(this.pict, pFilter.FromField) : undefined;
		let tmpAllowed;
		const tmpSpecResolver = this.pict.providers.SpecScopeResolver;
		if (tmpAnchor && pFilter.ThroughKey === 'IDMaterial' && tmpSpecResolver?.expandMaterialAnchorsUp)
		{
			// A material anchor pushed through a bridge expands UP — rows for the material OR its
			// parent categories match. getThroughIDs is single-anchor, so union per id (null/not-yet-
			// primed ancestors are skipped; all-null stays null = fail-open). Map strategy is fully
			// covered; a query-strategy ancestor whose slice was not primed simply contributes nothing.
			tmpAllowed = null;
			for (const tmpMaterialID of tmpSpecResolver.expandMaterialAnchorsUp(tmpAnchor))
			{
				const tmpSet = tmpResolver.getThroughIDs(pFilter, tmpMaterialID);
				if (tmpSet)
				{
					if (!tmpAllowed)
					{
						tmpAllowed = new Set();
					}
					for (const tmpID of tmpSet)
					{
						tmpAllowed.add(tmpID);
					}
				}
			}
		}
		else
		{
			tmpAllowed = tmpResolver.getThroughIDs(pFilter, tmpAnchor);
		}
		if (tmpAllowed && tmpAllowed.size > 0)
		{
			pFilters.push(`FBL~${pFilter.Field}~INN~${Array.from(tmpAllowed).join(',')}`);
		}
	}

	/**
	 * Apply a lab-scope clause for a DynamicFilters entry carrying `LabScope` ('Sample' | 'Test'), resolved by
	 * LabScopeResolver.
	 *
	 *   'Sample' → the PARENT labs a sample can ship to (the project's labs, else the parents of the labs that
	 *              can run the selected tests).
	 *   'Test'   → the LEAF labs that can run this row's test. Reads the per-row test from `TestFromRowField`
	 *              (tabular) or `TestFromField`. Capability is the only clause: the project's own labs and the
	 *              material defaults rank the list (`getPreferredLabIDs`, pinned by the selector) rather than
	 *              narrowing it, so the dropdown and auto-fill order one list the same way.
	 *
	 * The mandatory set becomes `FBL~Field~INN~<ids>`; an empty-but-resolved set constrains to none (Show All
	 * overrides) and a null resolve stays fail-open. Only capability can produce that empty set, because it is
	 * the only clause whose emptiness is a fact rather than a preference.
	 *
	 * @param {Array<string>} pFilters
	 * @param {Object} pFilter
	 * @param {Object} pInput
	 * @param {number|string|null} [pRowIndex]
	 * @param {Object} [pView]
	 */
	_applyLabScopeFilter(pFilters, pFilter, pInput, pRowIndex, pView)
	{
		if (this.isFilterOverridden(pInput, pRowIndex))
		{
			return;
		}
		const tmpResolver = this.pict.providers.LabScopeResolver;
		if (!tmpResolver || !pFilter.Field)
		{
			return;
		}
		let tmpAllowed = null;
		if (pFilter.LabScope === 'Test')
		{
			const tmpIDTest = this._readLabScopeTestID(pFilter, pInput, pRowIndex, pView);
			tmpAllowed = tmpResolver.getTestLabIDs(tmpIDTest);
		}
		else
		{
			tmpAllowed = tmpResolver.getSampleLabIDs();
		}
		if (tmpAllowed && tmpAllowed.size > 0)
		{
			pFilters.push(`FBL~${pFilter.Field}~INN~${Array.from(tmpAllowed).join(',')}`);
		}
		else if (tmpAllowed)
		{
			// Empty (but resolved) set — no lab qualifies for this test on this project. Constrain to none
			// (Show All overrides); a null resolve (no data on any axis) stays fail-open and omits the clause.
			pFilters.push(this._constrainToNoneClause(pFilter, pInput));
		}
	}

	/**
	 * The test a per-test lab selector is bound to: the row's own sibling cell when tabular, else a flat address.
	 *
	 * @param {Object} pFilter @param {Object} pInput @param {number|string|null} [pRowIndex] @param {Object} [pView]
	 * @return {any}
	 */
	_readLabScopeTestID(pFilter, pInput, pRowIndex, pView)
	{
		if (pRowIndex != null && pFilter.TestFromRowField && pInput.PictForm && pInput.PictForm.InformaryContainerAddress && pView)
		{
			const tmpRowAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pFilter.TestFromRowField);
			return this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRowAddress);
		}
		if (pFilter.TestFromField)
		{
			return this.pict.manifest.getValueByHash(this.pict, pFilter.TestFromField);
		}
		return undefined;
	}

	/**
	 * Whether a descriptor's filters are currently user-overridden ("show all"). When
	 * `PictForm.OverrideField` names a form address whose live value is truthy, the back-off-able
	 * filters (spec-year, `BackOff` static/dynamic) are dropped entirely for that picker — the manual
	 * companion to the automatic empty-result back-off.
	 *
	 * @param {object} pInput - The descriptor (reads `pInput.PictForm.ShowAllToggle` / `OverrideField`).
	 * @param {number|string} [pRowIndex] - Row index for a tabular picker; selects the per-row state.
	 * @return {boolean}
	 */
	isFilterOverridden(pInput, pRowIndex)
	{
		const tmpPictForm = pInput && pInput.PictForm ? pInput.PictForm : null;
		if (!tmpPictForm)
		{
			return false;
		}
		// Integrated "Show All" toggle: the override state persists at the <DataAddress>__ShowAll suffix.
		// Tabular is per-row, keyed by Hash with a _<rowIndex> suffix (the col DataAddress may be a nested
		// path like TestInstance.IDLab, which can't compose a flat state address). Legacy forms instead
		// point PictForm.OverrideField at an external checkbox address.
		let tmpField;
		if (tmpPictForm.ShowAllToggle && pRowIndex != null && (pInput.Hash || pInput.DataAddress))
		{
			tmpField = `AppData.DocumentData.ReportData.FormData.${pInput.Hash || pInput.DataAddress}__ShowAll_${pRowIndex}`;
		}
		else if (tmpPictForm.ShowAllToggle && pInput.DataAddress)
		{
			tmpField = `AppData.DocumentData.ReportData.FormData.${pInput.DataAddress}__ShowAll`;
		}
		else
		{
			tmpField = tmpPictForm.OverrideField;
		}
		if (!tmpField)
		{
			return false;
		}
		const tmpValue = this.pict.manifest.getValueByHash(this.pict, tmpField);
		return tmpValue === true || tmpValue === 1 || tmpValue === 'true' || tmpValue === '1';
	}
}

module.exports = EntityFilterEngineProvider;
